import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { supabase } from '../config/supabase';
import { useAuditUserId } from '../auth/AuthContext';
import { lineTypeOptions } from '../data/options';
import { usePersistentScrollRestoration, usePersistentViewState } from '../hooks/usePersistentViewState';

type MatchFormat = {
  id: string;
  workspace_id: string;
  configuration_name: string;
  match_format_name: string;
  description: string | null;
  number_of_singles_lines: number;
  number_of_doubles_lines: number;
  best_of_sets: number;
  final_set_type: 'standard_set' | 'match_tiebreak' | 'no_final_set';
  uses_match_tiebreak: boolean;
  match_tiebreak_target_score: number;
  require_win_by_two: boolean;
  is_active: boolean;
};

type FormatPosition = {
  id: string;
  league_configuration_id: string;
  position_code: string;
  display_name: string;
  display_order: number;
  line_type: string;
  required_player_count: number;
  required_male_count: number;
  required_female_count: number;
  is_doubles: boolean;
  counts_toward_fairness: boolean;
  is_active: boolean;
};

type Snack = {
  severity: 'success' | 'error';
  message: string;
};

const emptyFormat = {
  workspaceId: '',
  formatName: '',
  description: '',
  numberOfSinglesLines: 0,
  numberOfDoublesLines: 0,
  bestOfSets: 3,
  finalSetType: 'match_tiebreak' as MatchFormat['final_set_type'],
  usesMatchTiebreak: true,
  matchTiebreakTargetScore: 10,
  requireWinByTwo: true,
  isActive: true,
};

function activeLabel(value: boolean): string {
  return value ? 'Active' : 'Inactive';
}

function lineTypeLabel(value: string): string {
  return lineTypeOptions.find((option) => option.value === value)?.label || value;
}

function defaultPositionOrder(positions: FormatPosition[]): number {
  return positions.reduce((max, position) => Math.max(max, Number(position.display_order || 0)), 0) + 1;
}

function normalizedPositionPatch(lineType: string, patch: Partial<FormatPosition>): Partial<FormatPosition> {
  if (lineType === 'singles') {
    return { ...patch, required_player_count: 1, required_male_count: 0, required_female_count: 0, is_doubles: false };
  }
  if (lineType === 'doubles') {
    return { ...patch, required_player_count: 2, required_male_count: 0, required_female_count: 0, is_doubles: true };
  }
  if (lineType === 'mixed_doubles') {
    return { ...patch, required_player_count: 2, required_male_count: 1, required_female_count: 1, is_doubles: true };
  }
  return patch;
}

export function MatchFormats({ refreshLookups }: { refreshLookups: () => Promise<void> }) {
  const auditUserId = useAuditUserId();
  const {
    state: savedViewState,
    setState: setSavedViewState,
    stateKey: viewStateKey,
    loadedStateKey,
  } = usePersistentViewState({
    userId: auditUserId,
    routeKey: 'match-formats',
    defaultState: { selectedFormatId: null as string | null },
  });
  const [formats, setFormats] = useState<MatchFormat[]>([]);
  const [workspaces, setWorkspaces] = useState<Array<{ id: string; name: string }>>([]);
  const [positions, setPositions] = useState<FormatPosition[]>([]);
  const [selectedFormatId, setSelectedFormatId] = useState<string | null>(savedViewState.selectedFormatId);
  const [formatDialogOpen, setFormatDialogOpen] = useState(false);
  const [positionDialogOpen, setPositionDialogOpen] = useState(false);
  const [editingPosition, setEditingPosition] = useState<FormatPosition | null>(null);
  const [formatForm, setFormatForm] = useState(emptyFormat);
  const [positionForm, setPositionForm] = useState({
    position_code: '',
    display_name: '',
    display_order: 1,
    line_type: 'doubles',
    required_player_count: 2,
    required_male_count: 0,
    required_female_count: 0,
    is_active: true,
  });
  const [loading, setLoading] = useState(false);
  const [snack, setSnack] = useState<Snack | null>(null);
  const restoredViewStateKey = useRef('');
  const [viewStateReady, setViewStateReady] = useState(false);

  usePersistentScrollRestoration({ userId: auditUserId, routeKey: 'match-formats', ready: !loading });

  useEffect(() => {
    if (!auditUserId || loadedStateKey !== viewStateKey) {
      setViewStateReady(false);
      return;
    }
    if (restoredViewStateKey.current === viewStateKey) {
      setViewStateReady(true);
      return;
    }
    setSelectedFormatId(savedViewState.selectedFormatId);
    restoredViewStateKey.current = viewStateKey;
    setViewStateReady(true);
  }, [auditUserId, loadedStateKey, savedViewState, viewStateKey]);

  useEffect(() => {
    if (!viewStateReady || restoredViewStateKey.current !== viewStateKey) return;
    setSavedViewState({ selectedFormatId });
  }, [selectedFormatId, setSavedViewState, viewStateKey, viewStateReady]);

  const loadData = useCallback(async () => {
    setLoading(true);
    const [formatResult, positionResult, workspaceResult] = await Promise.all([
      supabase.from('league_configurations').select('*').is('deleted_at', null).order('configuration_name'),
      supabase.from('league_configuration_positions').select('*').is('deleted_at', null).order('display_order'),
      supabase.from('workspaces').select('id,name').eq('active_status', true).order('name'),
    ]);
    setLoading(false);

    const error = formatResult.error || positionResult.error || workspaceResult.error;
    if (error) {
      setSnack({ severity: 'error', message: error.message });
      return;
    }

    setFormats((formatResult.data || []) as unknown as MatchFormat[]);
    setPositions((positionResult.data || []) as unknown as FormatPosition[]);
    setWorkspaces(workspaceResult.data || []);
  }, []);

  useEffect(() => {
    void loadData();
  }, [loadData]);

  const positionsByFormat = useMemo(() => {
    const map = new Map<string, FormatPosition[]>();
    positions.forEach((position) => map.set(position.league_configuration_id, [...(map.get(position.league_configuration_id) || []), position]));
    map.forEach((rows) => rows.sort((a, b) => a.display_order - b.display_order || a.position_code.localeCompare(b.position_code)));
    return map;
  }, [positions]);

  const selectedFormat = selectedFormatId ? formats.find((format) => format.id === selectedFormatId) || null : null;
  const selectedPositions = selectedFormatId ? positionsByFormat.get(selectedFormatId) || [] : [];

  const openCreateFormat = () => {
    setSelectedFormatId(null);
    setFormatForm({ ...emptyFormat, workspaceId: workspaces.length === 1 ? workspaces[0].id : '' });
    setFormatDialogOpen(true);
  };

  const openEditFormat = (format: MatchFormat) => {
    setSelectedFormatId(format.id);
    setFormatForm({
      workspaceId: format.workspace_id,
      formatName: format.configuration_name,
      description: format.description || '',
      numberOfSinglesLines: format.number_of_singles_lines || 0,
      numberOfDoublesLines: format.number_of_doubles_lines || 0,
      bestOfSets: format.best_of_sets || 3,
      finalSetType: format.final_set_type || 'match_tiebreak',
      usesMatchTiebreak: format.uses_match_tiebreak,
      matchTiebreakTargetScore: format.match_tiebreak_target_score || 10,
      requireWinByTwo: format.require_win_by_two,
      isActive: format.is_active,
    });
    setFormatDialogOpen(true);
  };

  const saveFormat = async () => {
    if (!selectedFormatId && !formatForm.workspaceId) {
      setSnack({ severity: 'error', message: 'Workspace is required.' });
      return;
    }
    if (!formatForm.formatName.trim()) {
      setSnack({ severity: 'error', message: 'Format Name is required.' });
      return;
    }

    const payload = {
      configuration_name: formatForm.formatName.trim(),
      match_format_name: formatForm.formatName.trim(),
      description: formatForm.description || null,
      number_of_singles_lines: Number(formatForm.numberOfSinglesLines),
      number_of_doubles_lines: Number(formatForm.numberOfDoublesLines),
      best_of_sets: Number(formatForm.bestOfSets),
      final_set_type: formatForm.finalSetType,
      uses_match_tiebreak: formatForm.usesMatchTiebreak,
      match_tiebreak_target_score: Number(formatForm.matchTiebreakTargetScore),
      require_win_by_two: formatForm.requireWinByTwo,
      is_active: formatForm.isActive,
    };

    const result = selectedFormatId
      ? await supabase.from('league_configurations').update(payload).eq('id', selectedFormatId)
      : await supabase.from('league_configurations').insert({ ...payload, workspace_id: formatForm.workspaceId });

    if (result.error) {
      setSnack({ severity: 'error', message: result.error.message });
      return;
    }

    setFormatDialogOpen(false);
    setSnack({ severity: 'success', message: 'Match Format saved.' });
    await loadData();
    await refreshLookups();
  };

  const openCreatePosition = () => {
    if (!selectedFormatId) return;
    setEditingPosition(null);
    setPositionForm({
      position_code: '',
      display_name: '',
      display_order: defaultPositionOrder(selectedPositions),
      line_type: 'doubles',
      required_player_count: 2,
      required_male_count: 0,
      required_female_count: 0,
      is_active: true,
    });
    setPositionDialogOpen(true);
  };

  const openEditPosition = (position: FormatPosition) => {
    setEditingPosition(position);
    setPositionForm({
      position_code: position.position_code,
      display_name: position.display_name,
      display_order: position.display_order,
      line_type: position.line_type,
      required_player_count: position.required_player_count,
      required_male_count: position.required_male_count,
      required_female_count: position.required_female_count,
      is_active: position.is_active,
    });
    setPositionDialogOpen(true);
  };

  const validatePosition = (): string | null => {
    if (!positionForm.position_code.trim()) return 'Position Code is required.';
    if (!positionForm.display_name.trim()) return 'Display Name is required.';
    if (!Number.isInteger(Number(positionForm.display_order)) || Number(positionForm.display_order) <= 0) return 'Position Order is required.';
    if (!Number.isInteger(Number(positionForm.required_player_count)) || Number(positionForm.required_player_count) <= 0) return 'Players count is required.';
    if (positionForm.line_type === 'mixed_doubles' && (Number(positionForm.required_male_count) !== 1 || Number(positionForm.required_female_count) !== 1)) return 'For mixed doubles, Male and Female must both be 1.';
    if (positionForm.line_type === 'singles' && Number(positionForm.required_player_count) !== 1) return 'For singles, Players must be 1.';
    if (positionForm.line_type === 'doubles' && Number(positionForm.required_player_count) !== 2) return 'For doubles, Players must be 2.';
    return null;
  };

  const savePosition = async () => {
    if (!selectedFormatId) return;
    const validationError = validatePosition();
    if (validationError) {
      setSnack({ severity: 'error', message: validationError });
      return;
    }

    const basePayload = normalizedPositionPatch(positionForm.line_type, {
      position_code: positionForm.position_code.trim(),
      display_name: positionForm.display_name.trim(),
      display_order: Number(positionForm.display_order),
      line_type: positionForm.line_type,
      required_player_count: Number(positionForm.required_player_count),
      required_male_count: Number(positionForm.required_male_count),
      required_female_count: Number(positionForm.required_female_count),
      is_doubles: positionForm.line_type === 'doubles' || positionForm.line_type === 'mixed_doubles',
      counts_toward_fairness: true,
      is_active: positionForm.is_active,
    });

    const result = editingPosition
      ? await supabase.from('league_configuration_positions').update(basePayload).eq('id', editingPosition.id)
      : await supabase.from('league_configuration_positions').insert({ ...basePayload, league_configuration_id: selectedFormatId });

    if (result.error) {
      setSnack({ severity: 'error', message: result.error.message });
      return;
    }

    setPositionDialogOpen(false);
    setSnack({ severity: 'success', message: 'Position saved.' });
    await loadData();
    await refreshLookups();
  };

  const softDeletePosition = async (position: FormatPosition) => {
    if (!window.confirm(`Delete ${position.position_code}?`)) return;
    const { error } = await supabase
      .from('league_configuration_positions')
      .update({ deleted_at: new Date().toISOString(), delete_reason: 'Deleted from Match Formats screen', is_active: false })
      .eq('id', position.id);
    if (error) {
      setSnack({ severity: 'error', message: error.message });
      return;
    }
    setSnack({ severity: 'success', message: 'Position deleted.' });
    await loadData();
    await refreshLookups();
  };

  const movePosition = async (position: FormatPosition, direction: -1 | 1) => {
    const index = selectedPositions.findIndex((candidate) => candidate.id === position.id);
    const swap = selectedPositions[index + direction];
    if (!swap) return;
    const [first, second] = await Promise.all([
      supabase.from('league_configuration_positions').update({ display_order: swap.display_order }).eq('id', position.id),
      supabase.from('league_configuration_positions').update({ display_order: position.display_order }).eq('id', swap.id),
    ]);
    const error = first.error || second.error;
    if (error) {
      setSnack({ severity: 'error', message: error.message });
      return;
    }
    await loadData();
    await refreshLookups();
  };

  const onLineTypeChange = (lineType: string) => {
    const patch = normalizedPositionPatch(lineType, { line_type: lineType });
    setPositionForm((current) => ({ ...current, ...patch }));
  };

  return (
    <Stack spacing={2}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>Match Formats</Typography>
          <Typography variant="body2" color="text.secondary">Manage match formats and their lineup positions in one place.</Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openCreateFormat}>Add Match Format</Button>
      </Box>

      <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
        {loading && <LinearProgress />}
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700 }}>Format Name</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>Position Count</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {formats.map((format) => (
                <TableRow key={format.id} hover selected={selectedFormatId === format.id}>
                  <TableCell>{format.configuration_name}</TableCell>
                  <TableCell align="right">{positionsByFormat.get(format.id)?.length || 0}</TableCell>
                  <TableCell>{activeLabel(format.is_active)}</TableCell>
                  <TableCell align="right">
                    <Button size="small" startIcon={<VisibilityIcon />} onClick={() => setSelectedFormatId(format.id)}>Open</Button>
                    <Button size="small" startIcon={<EditIcon />} onClick={() => openEditFormat(format)}>Edit</Button>
                  </TableCell>
                </TableRow>
              ))}
              {formats.length === 0 && !loading && (
                <TableRow>
                  <TableCell colSpan={4}><Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>No match formats yet.</Typography></TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {selectedFormat && (
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Stack spacing={2}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2 }}>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>{selectedFormat.configuration_name}</Typography>
                <Typography variant="body2" color="text.secondary">{activeLabel(selectedFormat.is_active)}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {selectedFormat.number_of_singles_lines} singles · {selectedFormat.number_of_doubles_lines} doubles · Best of {selectedFormat.best_of_sets}
                  {selectedFormat.uses_match_tiebreak ? ` · ${selectedFormat.match_tiebreak_target_score}-point match tiebreak` : ''}
                </Typography>
              </Box>
              <Stack direction="row" spacing={1}>
                <Button startIcon={<AddIcon />} onClick={openCreatePosition}>Add Position</Button>
                <Button startIcon={<EditIcon />} onClick={() => openEditFormat(selectedFormat)}>Edit Format</Button>
              </Stack>
            </Box>

            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700 }}>Code</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Display Name</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>Order</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Line Type</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>Players</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>Male</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>Female</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {selectedPositions.map((position, index) => (
                    <TableRow key={position.id} hover>
                      <TableCell>{position.position_code}</TableCell>
                      <TableCell>{position.display_name}</TableCell>
                      <TableCell align="right">{position.display_order}</TableCell>
                      <TableCell>{lineTypeLabel(position.line_type)}</TableCell>
                      <TableCell align="right">{position.required_player_count}</TableCell>
                      <TableCell align="right">{position.required_male_count}</TableCell>
                      <TableCell align="right">{position.required_female_count}</TableCell>
                      <TableCell>{activeLabel(position.is_active)}</TableCell>
                      <TableCell align="right">
                        <Button size="small" disabled={index === 0} onClick={() => void movePosition(position, -1)}><KeyboardArrowUpIcon fontSize="small" /></Button>
                        <Button size="small" disabled={index === selectedPositions.length - 1} onClick={() => void movePosition(position, 1)}><KeyboardArrowDownIcon fontSize="small" /></Button>
                        <Button size="small" startIcon={<EditIcon />} onClick={() => openEditPosition(position)}>Edit</Button>
                        <Button size="small" color="error" startIcon={<DeleteIcon />} onClick={() => void softDeletePosition(position)}>Delete</Button>
                      </TableCell>
                    </TableRow>
                  ))}
                  {selectedPositions.length === 0 && (
                    <TableRow><TableCell colSpan={9}><Typography variant="body2" color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>No positions configured for this match format.</Typography></TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Stack>
        </Paper>
      )}

      <Dialog open={formatDialogOpen} onClose={() => setFormatDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{selectedFormatId ? 'Edit Match Format' : 'Add Match Format'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Format Information</Typography>
            {!selectedFormatId && (
              <FormControl fullWidth required>
                <InputLabel>Workspace</InputLabel>
                <Select label="Workspace" value={formatForm.workspaceId} onChange={(event) => setFormatForm((current) => ({ ...current, workspaceId: event.target.value }))}>
                  {workspaces.map((workspace) => <MenuItem key={workspace.id} value={workspace.id}>{workspace.name}</MenuItem>)}
                </Select>
              </FormControl>
            )}
            <TextField label="Format Name" required value={formatForm.formatName} onChange={(event) => setFormatForm((current) => ({ ...current, formatName: event.target.value }))} />
            <TextField label="Description" value={formatForm.description} multiline minRows={3} onChange={(event) => setFormatForm((current) => ({ ...current, description: event.target.value }))} />
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' }, gap: 2 }}>
              <TextField label="Number of Singles Lines" type="number" value={formatForm.numberOfSinglesLines} inputProps={{ min: 0, step: 1 }} onChange={(event) => setFormatForm((current) => ({ ...current, numberOfSinglesLines: Number(event.target.value) }))} />
              <TextField label="Number of Doubles Lines" type="number" value={formatForm.numberOfDoublesLines} inputProps={{ min: 0, step: 1 }} onChange={(event) => setFormatForm((current) => ({ ...current, numberOfDoublesLines: Number(event.target.value) }))} />
              <FormControl fullWidth>
                <InputLabel>Best Of Sets</InputLabel>
                <Select label="Best Of Sets" value={formatForm.bestOfSets} onChange={(event) => setFormatForm((current) => ({ ...current, bestOfSets: Number(event.target.value) }))}>
                  {[1, 3, 5].map((value) => <MenuItem key={value} value={value}>{value}</MenuItem>)}
                </Select>
              </FormControl>
              <FormControl fullWidth>
                <InputLabel>Final Set Type</InputLabel>
                <Select label="Final Set Type" value={formatForm.finalSetType} onChange={(event) => setFormatForm((current) => ({ ...current, finalSetType: event.target.value as MatchFormat['final_set_type'] }))}>
                  <MenuItem value="standard_set">Standard Set</MenuItem>
                  <MenuItem value="match_tiebreak">Match Tiebreak</MenuItem>
                  <MenuItem value="no_final_set">No Final Set</MenuItem>
                </Select>
              </FormControl>
              <TextField label="Match Tiebreak Target Score" type="number" disabled={!formatForm.usesMatchTiebreak} value={formatForm.matchTiebreakTargetScore} inputProps={{ min: 1, max: 99, step: 1 }} onChange={(event) => setFormatForm((current) => ({ ...current, matchTiebreakTargetScore: Number(event.target.value) }))} />
            </Box>
            <FormControlLabel control={<Checkbox checked={formatForm.usesMatchTiebreak} onChange={(event) => setFormatForm((current) => ({ ...current, usesMatchTiebreak: event.target.checked }))} />} label="Uses Match Tiebreak" />
            <FormControlLabel control={<Checkbox checked={formatForm.requireWinByTwo} onChange={(event) => setFormatForm((current) => ({ ...current, requireWinByTwo: event.target.checked }))} />} label="Require Win By Two" />
            <FormControlLabel control={<Checkbox checked={formatForm.isActive} onChange={(event) => setFormatForm((current) => ({ ...current, isActive: event.target.checked }))} />} label="Active" />
          </Stack>
        </DialogContent>
        <DialogActions><Button onClick={() => setFormatDialogOpen(false)}>Cancel</Button><Button variant="contained" onClick={() => void saveFormat()}>Save</Button></DialogActions>
      </Dialog>

      <Dialog open={positionDialogOpen} onClose={() => setPositionDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{editingPosition ? 'Edit Position' : 'Add Position'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Positions</Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' }, gap: 2 }}>
              <TextField label="Code" required value={positionForm.position_code} onChange={(event) => setPositionForm((current) => ({ ...current, position_code: event.target.value }))} />
              <TextField label="Display Name" required value={positionForm.display_name} onChange={(event) => setPositionForm((current) => ({ ...current, display_name: event.target.value }))} />
              <TextField label="Order" required type="number" value={positionForm.display_order} inputProps={{ min: 1, step: 1 }} onChange={(event) => setPositionForm((current) => ({ ...current, display_order: Number(event.target.value) }))} />
              <FormControl fullWidth required>
                <InputLabel>Line Type</InputLabel>
                <Select label="Line Type" value={positionForm.line_type} onChange={(event) => onLineTypeChange(event.target.value)}>
                  {lineTypeOptions.filter((option) => option.value !== '').map((option) => <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>)}
                </Select>
              </FormControl>
              <TextField label="Players" required type="number" value={positionForm.required_player_count} inputProps={{ min: 1, step: 1 }} onChange={(event) => setPositionForm((current) => ({ ...current, required_player_count: Number(event.target.value) }))} />
              <TextField label="Male" type="number" value={positionForm.required_male_count} inputProps={{ min: 0, step: 1 }} onChange={(event) => setPositionForm((current) => ({ ...current, required_male_count: Number(event.target.value) }))} />
              <TextField label="Female" type="number" value={positionForm.required_female_count} inputProps={{ min: 0, step: 1 }} onChange={(event) => setPositionForm((current) => ({ ...current, required_female_count: Number(event.target.value) }))} />
              <FormControlLabel control={<Checkbox checked={positionForm.is_active} onChange={(event) => setPositionForm((current) => ({ ...current, is_active: event.target.checked }))} />} label="Active" />
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions><Button onClick={() => setPositionDialogOpen(false)}>Cancel</Button><Button variant="contained" onClick={() => void savePosition()}>Save</Button></DialogActions>
      </Dialog>

      <Snackbar open={Boolean(snack)} autoHideDuration={5000} onClose={() => setSnack(null)}>
        {snack ? <Alert severity={snack.severity}>{snack.message}</Alert> : undefined}
      </Snackbar>
    </Stack>
  );
}
